import React, { useState } from "react";
import { motion } from "framer-motion";
import dr1Img from "../../images/digital-readiness/dr1.png";

// ================= OPTION SET =================

const deviceOptions = [
  { value: 1, label: "Smartphone" },
  { value: 2, label: "Tablet" },
  { value: 3, label: "Laptop" },
  { value: 4, label: "Desktop Computer" },
];

const hoursOptions = [
  { value: 1, label: "Less than 2 hours" },
  { value: 2, label: "3–4 hours" },
  { value: 3, label: "5–6 hours" },
  { value: 4, label: "7–8 hours" },
  { value: 5, label: "More than 8 hours" },
];

const frequencyOptions = [
  { value: 1, label: "Never" },
  { value: 2, label: "Rarely" },
  { value: 3, label: "Neutral" },
  { value: 4, label: "Often" },
  { value: 5, label: "Very often" },
];

// ================= QUESTION TEXT =================

const QUESTIONS = {
  1: "What technology devices do you use daily?",
  2: "How often do you use the internet in a day?",
  3: "I use Wi-Fi at home to access the internet.",
  4: "I use Wi-Fi at work to access the internet.",
  5: "I use Wi-Fi in public places to access the internet.",
  6: "I use my personal data to access the internet (including hotspots).",
};

const DRStep1 = () => {
  const student = JSON.parse(localStorage.getItem("student"));
  const studentId = student?.student_id;

  const [answers, setAnswers] = useState({
    1: [],   // multi-select
    2: null,
    3: null,
    4: null,
    5: null,
    6: null,
  });

  const [loading, setLoading] = useState(false);

  // ================= HANDLERS =================

  const handleSingleSelect = (questionId, value) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  const handleMultiSelect = (questionId, value) => {
    setAnswers((prev) => {
      const current = prev[questionId] || [];
      return {
        ...prev,
        [questionId]: current.includes(value)
          ? current.filter((v) => v !== value)
          : [...current, value],
      };
    });
  };

  const validateAnswers = () => {
    if (!answers[1] || answers[1].length === 0) return false;
    for (let i = 2; i <= 6; i++) {
      if (!answers[i]) return false;
    }
    return true;
  };

  // ================= SUBMIT =================

  const handleNext = async () => {
    if (!studentId) {
      alert("Student session not found. Please log in again.");
      window.location.href = "/login";
      return;
    }

    if (!validateAnswers()) {
      alert("Please answer all questions before continuing.");
      return;
    }

    setLoading(true);

    try {
      const payload = {
        student_id: Number(studentId),
        readiness_id: 1,
        next_step: 2,
        data: [
          // item 1 (multiple choice)
          ...answers[1].map((v) => ({
            item_id: 1,
            rating: v,
          })),
          // item 2–6 (single choice)
          ...[2, 3, 4, 5, 6].map((id) => ({
            item_id: id,
            rating: answers[id],
          })),
        ],
      };

      const res = await fetch(
        "http://localhost:3000/api/digital-readiness/save",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || "Failed to save data.");
      }

      localStorage.setItem(
        "student",
        JSON.stringify({
          ...student,
          dr_current_step: data.next_step || 2,
        })
      );

      window.location.href = "/digital-readiness/step2";
    } catch (err) {
      console.error(err);
      alert(err.message || "Failed to save data.");
    } finally {
      setLoading(false);
    }
  };

  // ================= UI =================

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-900 via-blue-700 to-blue-500 text-white flex flex-col items-center px-4 py-4">
      <div className="w-full max-w-5xl mb-2 text-xs text-blue-100">
        Step 1 of 6 – Digital Readiness Questionnaire
      </div>

      <div className="w-full max-w-5xl bg-white/10 rounded-2xl shadow-xl p-4 md:p-6 flex flex-col md:flex-row gap-5">
        {/* LEFT IMAGE */}
        <div className="md:w-2/5 flex justify-center items-center">
          <img
            src={dr1Img}
            alt="Digital readiness – demography"
            className="w-full max-w-xs rounded-xl bg-white border shadow"
          />
        </div>

        {/* RIGHT CONTENT */}
        <div className="md:w-3/5">
          <h1 className="text-xl md:text-2xl font-bold text-yellow-300 mb-1">
            Digital Readiness – Demography Survey
          </h1>
          <p className="text-xs md:text-sm text-blue-100 mb-3">
            Please answer the following questions honestly. All questions are
            required.
          </p>

          <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1">
            {/* Q1 MULTI */}
            <QuestionBlock title={`1. ${QUESTIONS[1]} (Select all that apply)`}>
              <div className="grid grid-cols-2 gap-2">
                {deviceOptions.map((opt) => (
                  <OptionButton
                    key={opt.value}
                    selected={answers[1].includes(opt.value)}
                    onClick={() => handleMultiSelect(1, opt.value)}
                    label={opt.label}
                  />
                ))}
              </div>
            </QuestionBlock>

            {/* Q2 SINGLE */}
            <QuestionBlock title={`2. ${QUESTIONS[2]}`}>
              {hoursOptions.map((opt) => (
                <OptionButton
                  key={opt.value}
                  selected={answers[2] === opt.value}
                  onClick={() => handleSingleSelect(2, opt.value)}
                  label={opt.label}
                  full
                />
              ))}
            </QuestionBlock>

            {/* Q3–Q6 FREQUENCY */}
            {[3, 4, 5, 6].map((qid) => (
              <QuestionBlock key={qid} title={`${qid}. ${QUESTIONS[qid]}`}>
                <div className="flex gap-1">
                  {frequencyOptions.map((opt) => (
                    <OptionButton
                      key={opt.value}
                      selected={answers[qid] === opt.value}
                      onClick={() => handleSingleSelect(qid, opt.value)}
                      label={opt.label}
                      small
                    />
                  ))}
                </div>
              </QuestionBlock>
            ))}
          </div>

          <div className="flex justify-end mt-4">
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              disabled={loading}
              onClick={handleNext}
              className="bg-yellow-300 text-blue-900 font-bold px-6 py-2 rounded-full shadow disabled:bg-gray-400"
            >
              {loading ? "Saving..." : "Next →"}
            </motion.button>
          </div>
        </div>
      </div>

      <p className="mt-3 text-[10px] text-blue-200">
        © 2025 Universitas Terbuka
      </p>
    </div>
  );
};

// ================= REUSABLE UI =================

const QuestionBlock = ({ title, children }) => (
  <div className="bg-blue-800/70 rounded-xl p-3 border border-blue-500/60">
    <p className="text-sm font-semibold mb-2">{title}</p>
    {children}
  </div>
);

const OptionButton = ({
  label,
  selected,
  onClick,
  full = false,
  small = false,
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`${
      full ? "w-full" : "flex-1"
    } ${small ? "text-[10px] py-1" : "text-xs py-2"}
      rounded-lg border transition
      ${
        selected
          ? "bg-yellow-300 text-blue-900 border-yellow-400 font-bold"
          : "bg-blue-700 text-white border-blue-400 hover:bg-blue-600"
      }`}
  >
    {label}
  </button>
);

export default DRStep1;
